import { useState } from "react";
import { useSEO } from "@/hooks/use-seo";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CheckCircle, ChevronDown, ChevronUp, Shield, ArrowRight, Calendar, Cpu, BarChart3, TrendingUp, Settings, Gamepad2, Gift, Wallet, AlertTriangle, Trophy, Scale } from "lucide-react";
import { Link } from "wouter";

const SECTIONS = [
  { href: "#zaklady", label: "Jak funguje automat" },
  { href: "#strategie", label: "Strategie" },
  { href: "#vyber-slotu", label: "Výběr slotu" },
  { href: "#bonusy", label: "Bonusy" },
  { href: "#bankroll", label: "Bankroll" },
  { href: "#myty", label: "Mýty" },
  { href: "#jackpoty", label: "Jackpoty" },
  { href: "#pravni", label: "Právní aspekty" },
];

const LEARNING_STEPS = [
  {
    n: "01",
    icon: Cpu,
    title: "RNG – generátor náhodných čísel",
    desc: "RNG (Random Number Generator) je základní technologie, která určuje výsledky každého otočení válců. Tento software neustále generuje náhodná čísla, která jsou při každém spinu převedena na konkrétní kombinaci symbolů.",
    bullets: [
      "Výsledky jsou zcela nepředvídatelné a každý spin je nezávislý na předchozích",
      "Výhra nebo prohra v předchozím kole nijak neovlivňuje další spin",
      "Moderní sloty jsou považovány za férové díky RNG",
      "Náhodnost je pravidelně kontrolována nezávislými testovacími laboratořemi",
      "Vybírejte pouze legální online casina s licencí od Ministerstva financí ČR",
    ],
  },
  {
    n: "02",
    icon: BarChart3,
    title: "RTP – návratnost hráči",
    desc: "RTP (Return To Player) představuje teoretickou návratnost hry vyjádřenou v procentech. Například slot s RTP 96 % znamená, že z každých vsazených 100 Kč se průměrně vrátí 96 Kč hráčům.",
    bullets: [
      "V českých legálních casinech se RTP pohybuje mezi 75–100 %",
      "Většina automatů má RTP kolem 95–98 %",
      "Vyšší RTP zlepšuje vaše šance na výhru",
      "RTP se projevuje až při velkém počtu spinů",
      "V krátkodobém hraní může být výsledek výrazně odlišný",
    ],
  },
  {
    n: "03",
    icon: TrendingUp,
    title: "Volatilita",
    desc: "Volatilita určuje frekvenci a velikost výher v konkrétním slotu. Tento parametr pomáhá hráčům pochopit, jaký styl hry mohou očekávat.",
    bullets: [
      "Nízká volatilita: menší výhry častěji, delší hraní",
      "Střední volatilita: vyvážený poměr rizika a výher",
      "Vysoká volatilita: delší období bez výher, ale možnost velmi vysokých výher",
      "Hry s vysokou volatilitou jsou vhodnější pro hráče s větším bankrollem",
    ],
  },
  {
    n: "04",
    icon: Settings,
    title: "Strategie a správné nastavení sázek",
    desc: "Žádná strategie nezaručí výhru na hracím automatu. Existují však přístupy, které mohou pomoci lépe kontrolovat riziko a prodloužit herní dobu.",
    bullets: [
      "Vybírejte sloty s vyšším RTP (ideálně nad 96 %)",
      "Nastavte sázky na 1–2 % z dostupného bankrollu",
      "Využívejte statistiky hry (RTP, volatilita, maximální výhra)",
      "Cílem není systém porazit, ale minimalizovat zbytečné ztráty",
    ],
  },
  {
    n: "05",
    icon: Gamepad2,
    title: "Výběr správného slotu",
    desc: "Výběr správného slotu může výrazně ovlivnit herní zkušenost. Různé hry se liší nejen grafikou a tématem, ale také matematickým modelem a stylem výplat.",
    bullets: [
      "Transparentní RTP: seriózní poskytovatelé uvádějí RTP v herním plánu",
      "Nezávislé testování: organizace jako eCOGRA nebo iTech Labs provádějí audity",
      "Renomovaní poskytovatelé: dlouhodobá reputace, licencované a auditované hry",
      "Zohledněte velikost bankrollu a své preference",
    ],
  },
  {
    n: "06",
    icon: Gift,
    title: "Casino bonusy a no deposit bonusy",
    desc: "Online casina nabízejí množství promo akcí a bonusů. Mezi nejčastější patří casino bonusy za vklad a no deposit bonusy, které umožňují hráčům získat výhody po registraci.",
    bullets: [
      "No deposit bonus: free spiny nebo kredit zdarma bez nutnosti vkladu",
      "Casino bonus za vklad: navyšuje první nebo další vklad (často o 100 %)",
      "Bonusy téměř vždy obsahují podmínky protočení",
      "Cashback vrací hráči část prohraných peněz za určité období",
      "Free spiny umožňují hrát vybrané sloty bez vlastního bankrollu",
    ],
  },
  {
    n: "07",
    icon: Wallet,
    title: "Bankroll management a limity",
    desc: "Správa bankrollu patří mezi nejdůležitější principy zodpovědného hraní. Bez jasně stanoveného rozpočtu může být hraní finančně náročné.",
    bullets: [
      "Bankroll by měl být pouze částka určená pro zábavu",
      "Před zahájením hry si určete maximální částku k utracení",
      "Využívejte nástroje kasina: limity vkladů, časové limity",
      "Disciplinovaný přístup snižuje riziko impulzivních rozhodnutí",
    ],
  },
  {
    n: "08",
    icon: AlertTriangle,
    title: "Časté mýty o automatech",
    desc: "Na internetu se často objevují tvrzení o tajných strategiích nebo systémech, které údajně dokážou automaty porazit. Ve skutečnosti jsou moderní sloty založené na technologii RNG.",
    bullets: [
      "Výsledky jsou nepředvídatelné – žádná strategie nemůže změnit matematiku hry",
      "\u201EZaručené systémy\u201C jsou marketingové triky",
      "Pokusy o hackování jsou prakticky nemožné a nelegální",
      "Každý spin je nezávislý na předchozích výsledcích",
    ],
  },
  {
    n: "09",
    icon: Trophy,
    title: "Jackpoty na automatech",
    desc: "Jackpoty představují nejvyšší možné výhry v online slotech. U progresivních jackpotů máte šanci vyhrát velmi vysoké částky, které výrazně převyšují běžné výhry.",
    bullets: [
      "Progresivní jackpoty rostou s každou sázkou hráčů",
      "Mohou být propojeny mezi více kasiny nebo platformami",
      "Pravděpodobnost zásahu je obvykle velmi nízká",
      "Některé sloty vyžadují maximální sázku pro aktivaci jackpotu",
      "Jackpot vnímejte jako potenciální bonus, ne jako strategii",
    ],
  },
  {
    n: "10",
    icon: Scale,
    title: "Právní a bezpečnostní aspekty",
    desc: "Bezpečné hraní začíná výběrem licencovaného online kasina. Regulovaní provozovatelé musí dodržovat přísná pravidla týkající se férovosti her a ochrany hráčů.",
    bullets: [
      "Licence zajišťuje certifikovaný RNG software",
      "Kasino pravidelně podstupuje nezávislé audity",
      "Hraní na regulovaných platformách snižuje riziko podvodu",
      "Vyšší úroveň bezpečnosti finančních transakcí",
    ],
  },
];

const VOLATILITY_TABLE = [
  { type: "Nízká volatilita", freq: "Časté", size: "Menší", risk: "Nízké", suitable: "Začátečníci, delší herní sezení" },
  { type: "Střední volatilita", freq: "Střední", size: "Střední", risk: "Střední", suitable: "Většina hráčů" },
  { type: "Vysoká volatilita", freq: "Řídké", size: "Vysoké", risk: "Vysoké", suitable: "Zkušení hráči s větším bankrollem" },
];

const FAQS = [
  { q: "Lze skutečně vyhrát na automatu?", a: "Krátkodobě ano – automaty vyplácejí výhry na základě RNG. Dlouhodobě má kasino vždy matematickou výhodu díky RTP nižšímu než 100 %. Výhra je možná, ale není zaručená." },
  { q: "Co je RTP a proč je důležité?", a: "RTP (Return To Player) je teoretická návratnost hry v procentech. Slot s RTP 96 % znamená, že z každých 100 Kč se průměrně vrátí 96 Kč. Vyšší RTP = lepší šance pro hráče." },
  { q: "Existuje strategie, jak porazit automat?", a: "Žádná strategie nedokáže změnit matematiku hry. Správný výběr slotu, rozumné sázky a bankroll management však mohou zlepšit herní zkušenost a snížit riziko rychlých ztrát." },
  { q: "Jak funguje progresivní jackpot?", a: "Malá část každé sázky hráčů je přidána do společného fondu, který roste, dokud jej někdo nevyhraje. Pravděpodobnost zásahu je velmi nízká, ale výhry mohou být extrémně vysoké." },
  { q: "Jsou online automaty férové?", a: "Ano, pokud hrajete v licencovaném kasinu. Legální online casina používají certifikovaný RNG software a jsou pravidelně auditována nezávislými laboratořemi jako eCOGRA nebo iTech Labs." },
  { q: "Co je volatilita slotu?", a: "Volatilita určuje frekvenci a velikost výher. Nízká volatilita = častější menší výhry. Vysoká volatilita = řidší, ale potenciálně vyšší výhry. Zvolte si podle svého bankrollu a stylu hry." },
];

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#eef1f7] last:border-0">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-4 text-left gap-4" data-testid={`faq-jak-vyhrat-${q.slice(0, 20).replace(/\s/g, "-")}`}>
        <span className="font-semibold text-gray-900 text-sm">{q}</span>
        {open ? <ChevronUp className="w-4 h-4 text-[#003da5] flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
      </button>
      {open && <p className="text-gray-600 text-sm leading-relaxed pb-4">{a}</p>}
    </div>
  );
}

export default function AutomatyJakVyhratPage() {
  useSEO({
    title: "Jak Vyhrát na Automatu 2026 – Tipy, RTP a strategie | HrajBonus",
    description: "Praktické tipy jak zvýšit šanci na výhru na online automatech. RTP, volatilita, bankroll management a výběr správného slotu. Hrajte chytře. 18+",
    url: "https://hrajbonus.com/automaty/jak-vyhrat",
  });

  return (
    <div className="min-h-screen bg-[#f5f7fb]" style={{ fontFamily: "Poppins, sans-serif" }}>
      <Header />

      <section className="relative pt-16 pb-0 overflow-hidden">
        <div className="absolute inset-0 z-0" style={{ background: "linear-gradient(135deg, #001a4d 0%, #002d7c 50%, #003da5 100%)" }} />
        <div className="absolute inset-0 z-0 opacity-5" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "40px 40px" }} />
        <div className="relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-14 sm:py-16">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-2 text-white/60 text-sm font-semibold" data-testid="breadcrumb-automaty-jak-vyhrat">
              <Link href="/automaty" className="hover:text-white transition-colors">Automaty</Link>
              <span>/</span>
              <span className="text-white/90">Jak vyhrát</span>
            </div>
            <div className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3 py-1.5 text-white/70 text-xs font-semibold" data-testid="badge-aktualizovano-jak-vyhrat">
              <Calendar className="w-3 h-3" /> Aktualizováno: 16. března 2026
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight mb-5" data-testid="text-jak-vyhrat-title">
            Jak vyhrát na automatu: Praktické tipy, bonusy a rizika
          </h1>
          <p className="text-white/75 text-lg leading-relaxed mb-6">
            Mnoho hráčů hledá odpověď na otázku, jak vyhrát na automatu. Pravdou však je, že žádný systém nedokáže hrací automaty dlouhodobě porazit. Správný výběr hry, práce s rozpočtem a využívání bonusů mohou výrazně zlepšit herní zkušenost a snížit riziko rychlých ztrát.
          </p>
          <div className="flex flex-wrap gap-3">
            {SECTIONS.map((s) => (
              <a href={s.href} key={s.href}>
                <div className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white/80 text-xs font-semibold hover:bg-white/15 transition-colors cursor-pointer">
                  <CheckCircle className="w-3.5 h-3.5" /> {s.label}
                </div>
              </a>
            ))}
          </div>
        </div>
        <div className="relative z-10">
          <svg viewBox="0 0 1440 44" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block" preserveAspectRatio="none" style={{ height: "44px" }}>
            <rect x="0" y="40" width="1440" height="4" fill="#f5f7fb" />
            <path d="M0 44L1440 44L1440 0C1200 35 720 40 0 0L0 44Z" fill="#f5f7fb" />
          </svg>
        </div>
      </section>

      <nav className="bg-[#f5f7fb] border-b border-[#dde4ee] sticky top-16 z-20" data-testid="automaty-nav">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
          <div className="flex items-center gap-1 overflow-x-auto py-3 -mx-1">
            {[
              { href: "/automaty", label: "Automaty přehled", active: false },
              { href: "/automaty/jak-vyhrat", label: "Jak vyhrát", active: true },
              { href: "/automaty/sloty-ktere-vyplaceji-nejvice", label: "Sloty které vyplácejí nejvíce", active: false },
            ].map(({ href, label, active }) => (
              <Link key={href} href={href}>
                <div
                  className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                    active
                      ? "bg-[#003da5] text-white"
                      : "text-gray-600 hover:bg-white hover:text-[#003da5]"
                  }`}
                  data-testid={`automaty-nav-${href.replace(/\//g, "-")}`}
                >
                  {label}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </nav>

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-10 space-y-8">

        <section id="zaklady">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-3xl font-black text-gray-900 mb-3" data-testid="text-section-zaklady">Základy: Jak funguje hrací automat</h2>
            <p className="text-gray-500 text-base mb-4">
              Online hrací automaty jsou digitální hry založené na matematickém modelu a algoritmech, které zajišťují náhodnost výsledků. Na rozdíl od klasických mechanických automatů nejsou výsledky ovlivněny fyzickým mechanismem, ale softwarem.
            </p>
            <p className="text-gray-500 text-base mb-8">
              Každý spin je samostatná událost a výsledek je generován okamžitě po spuštění otočení válců. Výplatní struktura, pravděpodobnosti i bonusové funkce jsou pevně definovány v herním programu. Díky tomu je každá hra transparentní a její matematika zůstává stejná bez ohledu na to, kdy nebo jak dlouho hráč hraje.
            </p>

            <div className="space-y-6">
              {LEARNING_STEPS.slice(0, 3).map((step, i) => {
                const Icon = step.icon;
                return (
                  <div key={step.n} className="relative" data-testid={`learning-step-${step.n}`}>
                    {i < 2 && (
                      <div className="absolute left-[23px] top-[56px] bottom-[-24px] w-0.5 bg-[#003da5]/15 z-0" />
                    )}
                    <div className="relative z-10 flex gap-5">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "#003da5" }}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                      </div>
                      <div className="flex-1 bg-[#f3f6fb] rounded-xl p-5 border border-[#dde4ee]/50">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-[#003da5] text-xs font-black bg-[#003da5]/10 px-2.5 py-1 rounded-full">{step.n}</span>
                          <h3 className="text-lg font-black text-gray-900">{step.title}</h3>
                        </div>
                        <p className="text-gray-600 text-sm leading-relaxed mb-4">{step.desc}</p>
                        <ul className="space-y-2">
                          {step.bullets.map((bullet, bi) => (
                            <li key={bi} className="flex items-start gap-2 text-sm text-gray-700">
                              <CheckCircle className="w-4 h-4 text-[#003da5] flex-shrink-0 mt-0.5" />
                              <span>{bullet}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section id="strategie">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-3xl font-black text-gray-900 mb-3" data-testid="text-section-strategie">Strategie a nastavení sázek</h2>
            <p className="text-gray-500 text-base mb-8">
              Je třeba si uvědomit, že žádná strategie nezaručí výhru na hracím automatu. Správná strategie spočívá v rozumném výběru hry, nastavení sázek a disciplinované správě bankrollu.
            </p>
            {(() => {
              const step = LEARNING_STEPS[3];
              const Icon = step.icon;
              return (
                <div className="relative" data-testid={`learning-step-${step.n}`}>
                  <div className="relative z-10 flex gap-5">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "#003da5" }}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div className="flex-1 bg-[#f3f6fb] rounded-xl p-5 border border-[#dde4ee]/50">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-[#003da5] text-xs font-black bg-[#003da5]/10 px-2.5 py-1 rounded-full">{step.n}</span>
                        <h3 className="text-lg font-black text-gray-900">{step.title}</h3>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed mb-4">{step.desc}</p>
                      <ul className="space-y-2">
                        {step.bullets.map((bullet, bi) => (
                          <li key={bi} className="flex items-start gap-2 text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-[#003da5] flex-shrink-0 mt-0.5" />
                            <span>{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </section>

        <section id="vyber-slotu">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-3xl font-black text-gray-900 mb-3" data-testid="text-section-vyber-slotu">Výběr správného slotu</h2>
            <p className="text-gray-500 text-base mb-8">
              Výběr správného slotu může výrazně ovlivnit herní zkušenost. Při výběru slotu by hráči měli zohlednit RTP, volatilitu, maximální výhru a reputaci poskytovatele.
            </p>
            {(() => {
              const step = LEARNING_STEPS[4];
              const Icon = step.icon;
              return (
                <div className="relative mb-8" data-testid={`learning-step-${step.n}`}>
                  <div className="relative z-10 flex gap-5">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "#003da5" }}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div className="flex-1 bg-[#f3f6fb] rounded-xl p-5 border border-[#dde4ee]/50">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-[#003da5] text-xs font-black bg-[#003da5]/10 px-2.5 py-1 rounded-full">{step.n}</span>
                        <h3 className="text-lg font-black text-gray-900">{step.title}</h3>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed mb-4">{step.desc}</p>
                      <ul className="space-y-2">
                        {step.bullets.map((bullet, bi) => (
                          <li key={bi} className="flex items-start gap-2 text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-[#003da5] flex-shrink-0 mt-0.5" />
                            <span>{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })()}

            <h3 className="text-xl font-black text-gray-900 mb-4">Jak si vybrat automat podle RTP a volatility</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse" data-testid="table-volatility">
                <thead>
                  <tr className="bg-[#f3f6fb]">
                    <th className="text-left px-4 py-3 font-bold text-gray-900 border border-[#dde4ee]">Typ</th>
                    <th className="text-left px-4 py-3 font-bold text-gray-900 border border-[#dde4ee]">Frekvence výher</th>
                    <th className="text-left px-4 py-3 font-bold text-gray-900 border border-[#dde4ee]">Velikost výher</th>
                    <th className="text-left px-4 py-3 font-bold text-gray-900 border border-[#dde4ee]">Riziko</th>
                    <th className="text-left px-4 py-3 font-bold text-gray-900 border border-[#dde4ee]">Vhodné pro</th>
                  </tr>
                </thead>
                <tbody>
                  {VOLATILITY_TABLE.map((row) => (
                    <tr key={row.type} className="hover:bg-[#f9fafb]">
                      <td className="px-4 py-3 font-semibold text-gray-800 border border-[#dde4ee]">{row.type}</td>
                      <td className="px-4 py-3 text-gray-600 border border-[#dde4ee]">{row.freq}</td>
                      <td className="px-4 py-3 text-gray-600 border border-[#dde4ee]">{row.size}</td>
                      <td className="px-4 py-3 text-gray-600 border border-[#dde4ee]">{row.risk}</td>
                      <td className="px-4 py-3 text-gray-600 border border-[#dde4ee]">{row.suitable}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-gray-500 text-sm mt-4">
              Informace o RTP lze obvykle najít v herním plánu slotu, na stránkách poskytovatele nebo v nezávislých testovacích reportech.
            </p>
          </div>
        </section>

        <section id="bonusy">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-3xl font-black text-gray-900 mb-3" data-testid="text-section-bonusy">Casino bonusy a no deposit bonusy</h2>
            <p className="text-gray-500 text-base mb-8">
              Online casina nabízejí množství promo akcí a bonusů, které vám dávají šanci vyhrát. Po registraci můžete získat free spiny nebo bonusový kredit a často máte možnost hrát zdarma.
            </p>
            {(() => {
              const step = LEARNING_STEPS[5];
              const Icon = step.icon;
              return (
                <div className="relative mb-6" data-testid={`learning-step-${step.n}`}>
                  <div className="relative z-10 flex gap-5">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "#003da5" }}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div className="flex-1 bg-[#f3f6fb] rounded-xl p-5 border border-[#dde4ee]/50">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-[#003da5] text-xs font-black bg-[#003da5]/10 px-2.5 py-1 rounded-full">{step.n}</span>
                        <h3 className="text-lg font-black text-gray-900">{step.title}</h3>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed mb-4">{step.desc}</p>
                      <ul className="space-y-2">
                        {step.bullets.map((bullet, bi) => (
                          <li key={bi} className="flex items-start gap-2 text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-[#003da5] flex-shrink-0 mt-0.5" />
                            <span>{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="grid sm:grid-cols-2 gap-4">
              <Link href="/bonusy" data-testid="link-no-deposit-bonusy">
                <div className="group flex items-center justify-between p-4 border border-[#003da5]/20 bg-[#f0f5ff] rounded-xl hover:border-[#003da5] hover:bg-[#e8f0fe] transition-colors cursor-pointer">
                  <div>
                    <div className="font-bold text-[#003da5] text-sm mb-1">Zobrazit nejlepší no deposit bonusy</div>
                    <p className="text-gray-500 text-xs">Free spiny a bonusový kredit zdarma bez nutnosti vkladu.</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#003da5] flex-shrink-0 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
              <Link href="/bonusy" data-testid="link-casino-bonusy">
                <div className="group flex items-center justify-between p-4 border border-[#003da5]/20 bg-[#f0f5ff] rounded-xl hover:border-[#003da5] hover:bg-[#e8f0fe] transition-colors cursor-pointer">
                  <div>
                    <div className="font-bold text-[#003da5] text-sm mb-1">Porovnat nejlepší casino bonusy</div>
                    <p className="text-gray-500 text-xs">Casino bonusy za vklad, které navyšují váš bankroll.</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#003da5] flex-shrink-0 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </div>
          </div>
        </section>

        <section id="bankroll">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-3xl font-black text-gray-900 mb-3" data-testid="text-section-bankroll">Hráčská disciplína: Bankroll a limity</h2>
            <p className="text-gray-500 text-base mb-8">
              Vaše disciplína při správě bankrollu je klíčová – bez jasně stanoveného rozpočtu může být hraní finančně náročné a hráč může rychle ztratit kontrolu nad výdaji.
            </p>
            {(() => {
              const step = LEARNING_STEPS[6];
              const Icon = step.icon;
              return (
                <div className="relative" data-testid={`learning-step-${step.n}`}>
                  <div className="relative z-10 flex gap-5">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "#003da5" }}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div className="flex-1 bg-[#f3f6fb] rounded-xl p-5 border border-[#dde4ee]/50">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-[#003da5] text-xs font-black bg-[#003da5]/10 px-2.5 py-1 rounded-full">{step.n}</span>
                        <h3 className="text-lg font-black text-gray-900">{step.title}</h3>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed mb-4">{step.desc}</p>
                      <ul className="space-y-2">
                        {step.bullets.map((bullet, bi) => (
                          <li key={bi} className="flex items-start gap-2 text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-[#003da5] flex-shrink-0 mt-0.5" />
                            <span>{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </section>

        <section id="myty">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-3xl font-black text-gray-900 mb-3" data-testid="text-section-myty">Časté mýty: Lze vyhrát systémem nebo hackem?</h2>
            <p className="text-gray-500 text-base mb-8">
              Na internetu se často objevují tvrzení o tajných strategiích. Ve skutečnosti jsou moderní sloty založené na technologii RNG, která zajišťuje náhodnost každého spinu.
            </p>
            {(() => {
              const step = LEARNING_STEPS[7];
              const Icon = step.icon;
              return (
                <div className="relative" data-testid={`learning-step-${step.n}`}>
                  <div className="relative z-10 flex gap-5">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "#003da5" }}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div className="flex-1 bg-[#f3f6fb] rounded-xl p-5 border border-[#dde4ee]/50">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-[#003da5] text-xs font-black bg-[#003da5]/10 px-2.5 py-1 rounded-full">{step.n}</span>
                        <h3 className="text-lg font-black text-gray-900">{step.title}</h3>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed mb-4">{step.desc}</p>
                      <ul className="space-y-2">
                        {step.bullets.map((bullet, bi) => (
                          <li key={bi} className="flex items-start gap-2 text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-[#003da5] flex-shrink-0 mt-0.5" />
                            <span>{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </section>

        <section id="jackpoty">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-3xl font-black text-gray-900 mb-3" data-testid="text-section-jackpoty">Jackpoty: Jak vyhrát jackpot na automatech</h2>
            <p className="text-gray-500 text-base mb-8">
              Jackpoty představují nejvyšší možné výhry v online slotech a patří mezi hlavní důvody, proč mnoho hráčů automaty zkouší. Jackpot by měl být vnímán spíše jako potenciální bonus než jako reálná strategie.
            </p>
            {(() => {
              const step = LEARNING_STEPS[8];
              const Icon = step.icon;
              return (
                <div className="relative" data-testid={`learning-step-${step.n}`}>
                  <div className="relative z-10 flex gap-5">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "#003da5" }}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div className="flex-1 bg-[#f3f6fb] rounded-xl p-5 border border-[#dde4ee]/50">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-[#003da5] text-xs font-black bg-[#003da5]/10 px-2.5 py-1 rounded-full">{step.n}</span>
                        <h3 className="text-lg font-black text-gray-900">{step.title}</h3>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed mb-4">{step.desc}</p>
                      <ul className="space-y-2">
                        {step.bullets.map((bullet, bi) => (
                          <li key={bi} className="flex items-start gap-2 text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-[#003da5] flex-shrink-0 mt-0.5" />
                            <span>{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </section>

        <section id="pravni">
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-3xl font-black text-gray-900 mb-3" data-testid="text-section-pravni">Právní a bezpečnostní aspekty</h2>
            <p className="text-gray-500 text-base mb-8">
              Bezpečné hraní začíná výběrem licencovaného online kasina. Regulovaní provozovatelé musí dodržovat přísná pravidla týkající se férovosti her, ochrany hráčů a transparentnosti finančních transakcí.
            </p>
            {(() => {
              const step = LEARNING_STEPS[9];
              const Icon = step.icon;
              return (
                <div className="relative" data-testid={`learning-step-${step.n}`}>
                  <div className="relative z-10 flex gap-5">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "#003da5" }}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div className="flex-1 bg-[#f3f6fb] rounded-xl p-5 border border-[#dde4ee]/50">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-[#003da5] text-xs font-black bg-[#003da5]/10 px-2.5 py-1 rounded-full">{step.n}</span>
                        <h3 className="text-lg font-black text-gray-900">{step.title}</h3>
                      </div>
                      <p className="text-gray-600 text-sm leading-relaxed mb-4">{step.desc}</p>
                      <ul className="space-y-2">
                        {step.bullets.map((bullet, bi) => (
                          <li key={bi} className="flex items-start gap-2 text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-[#003da5] flex-shrink-0 mt-0.5" />
                            <span>{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </section>

        <section>
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-5 shadow-sm">
            <p className="text-sm text-gray-600 leading-relaxed">
              <span className="font-bold text-gray-900">Nejlepší kasina pro hru na automatech: </span>
              <a href="/kasina" className="text-[#003da5] hover:underline font-semibold" data-testid="automaty-link-kasina">Přehled kasin</a>
              {" · "}
              <a href="/kasina/fastslots" className="text-[#003da5] hover:underline" data-testid="automaty-link-fastslots">Fast Slots</a>
              {" · "}
              <a href="/kasina/onlyspins" className="text-[#003da5] hover:underline" data-testid="automaty-link-onlyspins">OnlySpins</a>
              {" · "}
              <a href="/kasina/instant-casino" className="text-[#003da5] hover:underline" data-testid="automaty-link-instant">Instant Casino</a>
              {" · "}
              <a href="/kasina/golden-panda" className="text-[#003da5] hover:underline" data-testid="automaty-link-golden">Golden Panda</a>
              {" · "}
              <a href="/automaty/sloty-ktere-vyplaceji-nejvice" className="text-[#003da5] hover:underline" data-testid="automaty-link-sloty">Sloty s nejvyšším RTP</a>
              {" · "}
              <a href="/bonusy" className="text-[#003da5] hover:underline" data-testid="automaty-link-bonusy">Bonusy</a>
            </p>
          </div>
        </section>

        <section>
          <div className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-5" data-testid="text-section-faq">Časté dotazy</h2>
            <div>
              {FAQS.map(item => <FaqItem key={item.q} q={item.q} a={item.a} />)}
            </div>
          </div>
        </section>

        <div className="bg-white rounded-2xl border border-[#dde4ee] p-5 shadow-sm flex items-start gap-4">
          <Shield className="w-5 h-5 text-[#003da5] mt-0.5 flex-shrink-0" />
          <p className="text-gray-600 text-sm leading-relaxed">
            <span className="font-bold text-gray-900">Pouze pro hráče 18+. </span>
            Hrajte zodpovědně. Nastavte si limity a sázejte jen tolik, kolik si můžete dovolit prohrát. Pokud potřebujete pomoc, obraťte se na{" "}
            <a href="https://www.gamcare.org.uk/" target="_blank" rel="noopener noreferrer" className="text-[#003da5] hover:underline font-semibold" data-testid="link-gamcare">GamCare</a>.
          </p>
        </div>

        <div className="flex items-center justify-center gap-2 text-xs text-gray-400 font-medium pb-4">
          <Shield className="w-3.5 h-3.5" />
          <span>Pouze pro hráče 18+. Hrajte zodpovědně. Podmínky se vztahují.</span>
        </div>

      </div>
      <Footer />
    </div>
  );
}